import axios from 'axios'
import Wrapper from '../../../components/Wrapper';
import React, { useEffect, useState } from 'react';
import { User } from '../../../models/user';
import Title from '../../../components/Title';

import Typography from '@mui/material/Typography';
import { NavLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import { Card, CardContent, Grid } from '@mui/material';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { SimpleColumn } from '../../../components/table/interface';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { isSuperAdminUser } from '../../../utils/authScope';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
type RowId = string | number;

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const Users: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Administrar Usuarios";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [users, setUsers] = useState<User[]>([]);
    const navigate = useNavigate();
    const { drawerOpen } = useDrawer();
    const [selectedIds, setSelectedIds] = useState<RowId[]>([]);
    const {
        canCreate,
        canRead,
        canUpdate,
        canDelete,
        canToggleEnabled,
        authUserData,
        isLoadingPermissions,
    } = useResourcePermissions('/administrar/usuarios');

    const [associationsOpen, setAssociationsOpen] = useState(false);
    const [associationsLoading, setAssociationsLoading] = useState(false);
    const [associationsOffices, setAssociationsOffices] = useState<Office[]>([]);
    const [associationsCollects, setAssociationsCollects] = useState<Collect[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
    const [officeId, setOfficeId] = useState<string>('');
    const [collectId, setCollectId] = useState<string>('');
    const [officesLoaded, setOfficesLoaded] = useState(false);
    const isSuperAdmin = isSuperAdminUser(authUserData);

    const modalStyle = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: '90vw',
        maxWidth: 500,
        height: '80vh',
        maxHeight: 500,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
    };

    const openAssociationsModal = async (userId: RowId) => {
        const numericId = typeof userId === 'string' ? Number(userId) : userId;
        if (!Number.isFinite(numericId)) return;

        setAssociationsOpen(true);
        setAssociationsLoading(true);
        setAssociationsOffices([]);
        setAssociationsCollects([]);

        try {
            // Oficinas asociadas
            const { data } = await axios.get(`useroffice/${numericId}`);
            const offices = Array.isArray(data?.offices) ? data.offices : [];
            const usersOffice = Array.isArray(data?.users) ? data.users : [];
            const officeIdsFromUsers = usersOffice
                .map((u: any) => Number(u?.office_id))
                .filter((n: number) => Number.isFinite(n));
            const officeIds = officeIdsFromUsers.length > 0 ? officeIdsFromUsers : offices.map((o: any) => Number(o?.id)).filter((n: number) => Number.isFinite(n));

            setAssociationsOffices(offices);

            // Cobros asociados (por cada oficina)
            if (officeIds.length > 0) {
                const results = await Promise.all(
                    officeIds.map((oid: number) =>
                        axios
                            .get(`usercollect/${oid}/${numericId}`)
                            .catch(() => null)
                    )
                );

                const collectsFromAllOffices: Collect[] = [];
                results.forEach((r: any) => {
                    const selectedCollect = Array.isArray(r?.data?.selectedCollect) ? r.data.selectedCollect : [];
                    collectsFromAllOffices.push(...selectedCollect);
                });

                // deduplicar por id del cobro
                const uniqueById = new Map<number, Collect>();
                collectsFromAllOffices.forEach((c: Collect) => {
                    const cid = Number((c as any)?.id);
                    if (Number.isFinite(cid)) uniqueById.set(cid, c);
                });

                setAssociationsCollects(Array.from(uniqueById.values()));
            }
        } catch (error) {
            console.error('Error loading user associations:', error);
            showAlert('Error al cargar oficinas y cobros del usuario', 'error');
        } finally {
            setAssociationsLoading(false);
        }
    };

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => {
                return response;
            },
            error => {
                if (error.response && error.response.status === 401) {
                    navigate('/login');
                    return; // Salimos temprano y no devolvemos el error rechazado.
                }
            }
        );
        return () => {
            axios.interceptors.response.eject(interceptor);
        }
    }, [navigate]);
    useEffect(() => {
        if (isLoadingPermissions) {
            return;
        }

        const fetchOffices = async () => {
            try {
                const officesUrl = isSuperAdmin ? 'alloffices' : 'offices';
                const officesResponse = await axios.get(officesUrl);
                const officeOptions = Array.isArray(officesResponse?.data?.data)
                    ? officesResponse.data.data
                    : [];

                setOffices(officeOptions);

                if (isSuperAdmin) {
                    setOfficeId('');
                    return;
                }

                if (officeOptions.length > 0) {
                    setOfficeId(String(officeOptions[0].id));
                }
            } catch (error) {
                console.error('Error loading offices: ', error);
                setOffices([]);
                setOfficeId('');
            } finally {
                setOfficesLoaded(true);
            }
        };

        fetchOffices();
    }, [isLoadingPermissions, isSuperAdmin]);

    useEffect(() => {
        if (!officesLoaded) {
            return;
        }

        if (!isSuperAdmin && offices.length > 0 && !officeId) {
            return;
        }

        const fetchData = async () => {
            showLoading();
            try {
                const { data } = await axios.get('users', {
                    params: {
                        ...(officeId ? { office_id: officeId } : {}),
                        ...(collectId ? { collect_id: collectId } : {}),
                    },
                });
                setUsers(data.data || []);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                console.error('Error fetching data: ', error);
                showAlert('Error fetching data!', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchData();
    }, [collectId, officeId, isSuperAdmin, offices.length, officesLoaded]);

    useEffect(() => {
        const fetchCollectsByOffice = async () => {
            if (!officeId) {
                setCollectsOffice([]);
                setCollectId('');
                return;
            }

            try {
                const { data } = await axios.get(`collectsoffice/${officeId}`);
                const collects = Array.isArray(data) ? data : [];
                setCollectsOffice(collects);

                if (isSuperAdmin) {
                    if (collectId && !collects.some((collect: Collect) => String(collect.id) === String(collectId))) {
                        setCollectId('');
                    }
                    return;
                }

                if (collects.length === 0) {
                    setCollectId('');
                    return;
                }

                const currentCollectAllowed = collects.some(
                    (collect: Collect) => String(collect.id) === String(collectId)
                );
                if (!currentCollectAllowed) {
                    setCollectId(String(collects[0].id));
                }
            } catch (error) {
                console.error('Error loading collects: ', error);
                setCollectsOffice([]);
                setCollectId('');
            }
        };

        fetchCollectsByOffice();
    }, [collectId, isSuperAdmin, officeId]);

    const handleDeleteUser = async (id: RowId) => {
        if (!canDelete) return;

        const numericId = typeof id === 'string' ? Number(id) : id;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }

        const confirmDelete = window.confirm('¿Estás seguro de que deseas eliminar este usuario?');
        if (!confirmDelete) return;

        try {
            showLoading();
            await axios.delete(`users/${numericId}`);
            setUsers(prev => prev.filter((user: User) => user.id !== numericId));
            setSelectedIds(prev => prev.filter(currentId => currentId !== numericId));
            showAlert('Usuario eliminado correctamente', 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(error?.response?.data?.message || 'Error al eliminar el usuario', 'error');
        } finally {
            hideLoading();
        }
    };

    const handleToggleUserState = async (id: RowId, checked: boolean) => {
        if (!canToggleEnabled) return;

        const numericId = typeof id === 'string' ? Number(id) : id;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }

        try {
            showLoading();
            await axios.put('users/state', {
                users: [{ id: numericId, state: checked ? 1 : 0 }],
            });
            setUsers((prev) =>
                prev.map((user) =>
                    Number(user.id) === numericId
                        ? Object.assign(new User(), user, { state: checked ? 1 : 0 })
                        : user
                )
            );
            showAlert(
                checked ? 'Usuario habilitado correctamente' : 'Usuario inhabilitado correctamente',
                'success'
            );
        } catch (error: any) {
            console.error(error);
            showAlert(error?.response?.data?.message || 'Error al actualizar el estado del usuario', 'error');
        } finally {
            hideLoading();
        }
    };

    const simpleColumns: SimpleColumn[] = [
        { field: 'idnumber', headerName: 'Cédula', minWidth: 100, maxWidth: 150 },
        { field: 'username', headerName: 'Usuario', minWidth: 120, maxWidth: 200 },
        {
            field: 'fullName',
            headerName: 'Nombres y Apellidos',
            minWidth: 180, maxWidth: 260,
            renderCell: ({ row }) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
        },
        { field: 'role', headerName: 'Perfil', minWidth: 80, maxWidth: 200 },
        ...(canToggleEnabled ? [{
            field: 'stateLabel',
            headerName: 'Estado',
            minWidth: 170,
            maxWidth: 190,
            renderCell: ({ row }: { row: any }) => (
                <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                        <Switch
                            size="small"
                            checked={Number(row.state ?? 1) === 1}
                            onChange={(event) => handleToggleUserState(row.id, event.target.checked)}
                            disabled={!canToggleEnabled}
                        />
                    }
                    label={Number(row.state ?? 1) === 1 ? 'Habilitado' : 'Inhabilitado'}
                />
            ),
        }] : []),

        // ✅ NUEVAS columnas de acción
        {
            field: 'opt_edit',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/usuarios/${row.id}/editar`}
                >
                    <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canDelete}
                    deniedTooltip="Sin permiso para eliminar"
                    tooltip="Eliminar"
                    variant="contained"
                    color="error"
                    sx={simpleTableIconButtonSx}
                    onClick={() => handleDeleteUser(row.id)}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_view_associations',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canRead}
                    deniedTooltip="Sin permiso para consultar asociaciones"
                    tooltip="Ver oficinas y cobros"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    onClick={() => openAssociationsModal(row.id)}
                >
                    <MapsHomeWorkIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];


    const rows = users.map((user: User) => ({
        ...user,
        role: user.role.name,
        username: user.username,
        id: user.id,
        idnumber: user.idnumber,
        fullName: `${user.first_name} ${user.last_name}`,
        state: Number((user as any).state ?? 1),
        stateLabel: Number((user as any).state ?? 1) === 1 ? 'Habilitado' : 'Inhabilitado',
    }));

    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Usuarios</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/usuarios/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card
                sx={{
                    width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                }}
            >
                <form onSubmit={(event) => event.preventDefault()}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="users-office-filter-label">Seleccionar Oficina</InputLabel>
                                    {offices && offices.length > 0 ? (
                                        <Select
                                            label="Seleccionar Oficina"
                                            value={officeId}
                                            size="small"
                                            id="users-office-filter"
                                            labelId="users-office-filter-label"
                                            onChange={(event) => setOfficeId(String(event.target.value))}
                                        >
                                            {isSuperAdmin && (
                                                <MenuItem value="">
                                                    Todas las oficinas
                                                </MenuItem>
                                            )}
                                            {offices.map((office: Office) => (
                                                <MenuItem key={office.id} value={String(office.id)}>
                                                    {office.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} xl={6}>
                                <FormControl fullWidth disabled={!officeId}>
                                    <InputLabel id="users-collect-filter-label">Seleccionar Cobro</InputLabel>
                                    <Select
                                        label="Seleccionar Cobro"
                                        value={collectId}
                                        size="small"
                                        id="users-collect-filter"
                                        labelId="users-collect-filter-label"
                                        onChange={(event) => setCollectId(String(event.target.value))}
                                    >
                                        {isSuperAdmin && (
                                            <MenuItem value="">
                                                Todos los cobros
                                            </MenuItem>
                                        )}
                                        {collectsOffice.map((collect: Collect) => (
                                            <MenuItem key={collect.id} value={String(collect.id)}>
                                                {collect.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>

                <Box>
                    {users.length > 0 ? (
                        <CardContent
                            sx={{
                                width: (theme) =>
                                    stickyListRegionWidth(theme, drawerOpen),
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                overflowX: 'auto',
                                pt: 0,
                                pb: 4,
                            }}
                        >
                            <SimpleCustomTable
                                rows={rows}
                                columns={simpleColumns}
                                checkboxSelection
                                selectionModel={selectedIds}
                                onRowSelectionModelChange={setSelectedIds}
                                maxHeight={400}
                                rowHeight={isMobile ? 36 : 24}
                                exportCsv
                                exportFileName="usuarios.csv"
                                columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete', 'opt_view_associations'] }]}
                                wrapCellContent
                            />
                        </CardContent>
                    ) : (
                        <EmptyStateMessage message="No existen registros" align="left" />
                    )}
                </Box>
            </Card>

    <Modal
        open={associationsOpen}
        onClose={() => setAssociationsOpen(false)}
        aria-labelledby="associations-modal-title"
        aria-describedby="associations-modal-description"
    >
        <Box sx={modalStyle}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "primary.main",
                    color: "white",
                }}
            >
                <Typography variant="h6">Oficinas y cobros del usuario</Typography>
                <IconButton
                    onClick={() => setAssociationsOpen(false)}
                    sx={{ color: "white" }}
                    aria-label="cerrar"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
                {associationsLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Consulta en base a sus asignaciones
                        </Typography>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            Oficinas ({associationsOffices.length})
                        </Typography>
                        <List dense>
                            {associationsOffices.length > 0 ? (
                                associationsOffices.map((o: Office) => (
                                    <ListItem key={o.id} disableGutters>
                                        <ListItemText primary={o.name} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem disableGutters>
                                    <ListItemText primary="No hay oficinas asignadas" />
                                </ListItem>
                            )}
                        </List>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            Cobros ({associationsCollects.length})
                        </Typography>
                        <List dense>
                            {associationsCollects.length > 0 ? (
                                associationsCollects.map((c: Collect) => (
                                    <ListItem key={c.id} disableGutters>
                                        <ListItemText
                                            primary={c.name}
                                            secondary={c.office?.name ? `Oficina: ${c.office.name}` : undefined}
                                        />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem disableGutters>
                                    <ListItemText primary="No hay cobros asignados" />
                                </ListItem>
                            )}
                        </List>
                    </>
                )}
            </Box>
        </Box>
    </Modal>
        </HeaderResponsive >
    )
}

export default Users
